import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, HelpCircle, Search } from "lucide-react";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { useSiteTexts } from "../lib/siteTexts";
import { SITE_URL } from "../lib/constants";
import { PAGE_TEXTS } from "../data/pageTexts";
import { asList, catLabel, useArticles, useKbCategories } from "../lib/kennisbank";
import { Crumbs } from "../components/product/ProductHero";
import { ArticleCard, ArticleImage } from "../components/kennisbank/ArticleCard";
import { AdviceEndCta } from "../components/info/AdviceBlocks";

const CFG = PAGE_TEXTS.kennisbank;

const Featured = ({ a, cats }) => (
  <Link to={`/blog/${a.slug}`} className="card card-hover group grid overflow-hidden lg:grid-cols-12" data-testid="kb-featured">
    <div className="aspect-[16/10] overflow-hidden bg-slate-100 lg:col-span-7 lg:aspect-auto lg:min-h-[340px]">
      <ArticleImage image={a.image} priority sizes="(min-width: 1024px) 700px, calc(100vw - 32px)" className="transition-transform duration-700 group-hover:scale-[1.03]" />
    </div>
    <div className="flex flex-col justify-center p-6 sm:p-8 lg:col-span-5 lg:p-10">
      <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">Uitgelicht · {catLabel(cats, a.category)}</p>
      <h2 className="mt-3 text-[24px] font-bold leading-tight text-ink transition-colors group-hover:text-maas sm:text-[28px]">{a.title}</h2>
      <p className="mt-3 text-[15.5px] leading-relaxed text-slate-600">{a.excerpt}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas">Lees het artikel <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
    </div>
  </Link>
);

const Filters = ({ cats, counts, active, set }) => (
  <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]" role="group" aria-label="Filter op onderwerp" data-testid="kb-filters">
    {[["", "Alle artikelen", counts.__all], ...asList(cats).filter((c) => counts[c.key]).map((c) => [c.key, c.label, counts[c.key]])].map(([key, label, n]) => (
      <button key={key || "all"} type="button" onClick={() => set(key)} aria-pressed={active === key} data-testid={`kb-filter-${key || "all"}`}
        className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-[14px] font-medium transition-colors ${active === key ? "bg-ink text-white" : "bg-white text-ink ring-1 ring-slate-200 hover:ring-maas"}`}>
        {label} <span className={`text-[12px] ${active === key ? "text-slate-300" : "text-slate-400"}`}>{n}</span>
      </button>
    ))}
  </div>
);

export default function Blog() {
  const t = useSiteTexts("kennisbank");
  const seo = t(CFG.blocks.seo);
  const hero = t(CFG.blocks.hero);
  const articles = useArticles();
  const cats = useKbCategories();
  const [sp, setSp] = useSearchParams();
  const active = sp.get("categorie") || "";
  const q = sp.get("zoek") || "";
  const list = asList(articles);
  const counts = useMemo(() => list.reduce((m, a) => ({ ...m, [a.category]: (m[a.category] || 0) + 1 }), { __all: list.length }), [list]);
  const needle = q.trim().toLowerCase();
  const shown = list.filter((a) => (!active || a.category === active) && (!needle || `${a.title} ${a.excerpt}`.toLowerCase().includes(needle)));
  const featured = !active && !needle ? shown.find((a) => a.featured) : null;
  const rest = shown.filter((a) => a !== featured);
  const setParam = (k, v) => setSp((prev) => { const n = new URLSearchParams(prev); v ? n.set(k, v) : n.delete(k); return n; }, { replace: true });
  useSEO({
    title: seo.title, description: seo.body, path: "/blog",
    breadcrumbs: buildBreadcrumbsJsonLd([{ label: "Home", href: "/" }, { label: "Kennisbank", href: "/blog" }]),
    jsonLd: list.length ? { "@context": "https://schema.org", "@type": "CollectionPage", name: hero.title, description: seo.body, url: `${SITE_URL}/blog`,
      mainEntity: { "@type": "ItemList", itemListElement: list.slice(0, 30).map((a, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/blog/${a.slug}`, name: a.title })) } } : null,
  });
  return (
    <div data-testid="kb-index-page">
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="hero-grid absolute inset-y-0 right-0 hidden w-[45%] lg:block" aria-hidden="true" />
        <div className="container-tight relative pb-10 pt-6 sm:pt-8 lg:pb-14">
          <Crumbs items={[{ label: "Home", href: "/" }, { label: "Kennisbank", href: "/blog" }]} />
          <div className="reveal mt-8 max-w-2xl">
            <p className="eyebrow">Kennisbank · {list.length || ""} artikelen</p>
            <h1 className="h1 mt-3 text-balance" data-testid="kb-index-title">{hero.title}</h1>
            <p className="lead mt-4">{hero.body}</p>
            <label className="relative mt-7 block max-w-lg">
              <span className="sr-only">Zoek in de kennisbank</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input type="search" value={q} onChange={(e) => setParam("zoek", e.target.value)} placeholder="Zoek op onderwerp, bijvoorbeeld 'schuifpui'"
                className="h-14 w-full rounded-2xl border border-slate-300 bg-white pl-12 pr-4 text-[16px] text-ink shadow-sm placeholder:text-slate-400 focus:border-maas focus:outline-none focus:ring-2 focus:ring-maas/20" data-testid="kb-search" />
            </label>
          </div>
        </div>
      </section>
      <section className="section pt-10" aria-label="Artikelen">
        <div className="container-tight">
          <Filters cats={cats} counts={counts} active={active} set={(v) => setParam("categorie", v)} />
          {articles === undefined ? <div className="mt-8 grid gap-5 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-slate-100" />)}</div> : (
            <>
              {featured && <div className="mt-8"><Featured a={featured} cats={cats} /></div>}
              {rest.length > 0 && <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="kb-grid">{rest.map((a) => <li key={a.slug}><ArticleCard a={a} cats={cats} /></li>)}</ul>}
              {!shown.length && <p className="mt-10 rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-600" data-testid="kb-empty">Geen artikelen gevonden. Probeer een ander zoekwoord of bekijk <Link to="/veelgestelde-vragen" className="font-semibold text-maas hover:underline">de veelgestelde vragen</Link>.</p>}
            </>
          )}
          <Link to="/veelgestelde-vragen" className="card card-hover group mt-12 flex items-center gap-4 p-5 sm:p-6" data-testid="kb-faq-link">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-maas-soft text-maas"><HelpCircle className="h-5 w-5" aria-hidden="true" /></span>
            <span className="flex-1"><span className="block text-[16px] font-semibold text-ink group-hover:text-maas">Op zoek naar een kort antwoord?</span><span className="block text-[14px] text-slate-600">Bekijk alle veelgestelde vragen over kozijnen, puien, deuren, glas, montage en subsidie.</span></span>
            <ArrowRight className="h-5 w-5 text-maas transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </div>
      </section>
      <AdviceEndCta title="Van informatie naar een prijs voor uw woning" location="kb-index" />
    </div>
  );
}
